import { Content } from 'pdfmake/interfaces';

import {
  IPdfDefinitions,
  PdfContentSetting,
  ColumnDefaultStyle,
  IPdfColumn,
  IPdfBulkOptions,
  PdfSelectColumn,
} from '@app/shared/common/make-pdf/v2/pdf.type';
import {
  PdfContentType,
  IPdfData,
  getSearchHeaderDefine,
} from '@app/shared/common/make-pdf/v2';
import {
  head,
  includes,
  map,
  compact,
  get,
  isEqual,
  sumBy,
  filter,
  find,
} from 'lodash';
import { STAND_WORKSHEET_PDF_HEADER_VALUES } from '@app/shared/types/enums/stand-worksheet-pdf.enum';
import { IInformationData } from '@app/shared/types/entities/stand-worksheet';
import { formatDiffNumberAndPercentage } from '@app/shared/utils/percentage.utils';
import { IStandWorkSheetPDFHeadersOption } from '@app/shared/common/make-pdf/pdf-options.type';

import { STAND_WORKSHEET_HEADER_INFO_MESSAGE } from './stand-worksheet-info-message';
import {
  getEntryFormPdfColumns,
  getInformationFormHeaderWidth,
  getInformationPdfColumns,
  getPdfTextColor,
} from './stand-worksheet-pdf.utils';
import { IPdfTableHeader } from '@app/shared/common/make-pdf/pdf-table-header.type';
import { GLOBAL_CHARGEABLE_TEXT } from '@config/global-constants';
import { getSingleExportFileName } from '@app/shared/utils/common-utils';
import {
  STAND_WORKSHEET_PDF_SELECTED_COLUMNS,
  STAND_WORKSHEET_PDF_SELECTED_UP_COLUMNS,
} from './stand-worksheet.constants';
import { UiActions } from '@app/store/actions';
import { I18n } from '@app/i18n/strings-keys';

const buildSinglePdfFileName = (pdfData: IPdfData): string =>
  `Stand Worksheet - ${pdfData?.siteName}`;

const onCacheSelectItems = (
  pdfHeaders: PdfSelectColumn[],
  uiActions: UiActions,
): void => {
  const requiredPdfHeaders = map(pdfHeaders, (item: PdfSelectColumn) => ({
    ...item,
    required: true,
  }));
  uiActions.savePrintColumns({
    [STAND_WORKSHEET_PDF_SELECTED_COLUMNS]: requiredPdfHeaders,
  });
};

const buildSWSPdfConfig = (
  data: IPdfDefinitions['data'],
  {
    entryFormHeaders,
    isOffPostClosureSetting,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    uiActions,
    pdfSelectColumn,
    i18nService,
  }: IStandWorkSheetPDFHeadersOption,
  searched: boolean = false,
  bulkOptions?: IPdfBulkOptions,
): IPdfDefinitions => {
  const onClose = (pdfHeaders: PdfSelectColumn[]): void => {
    const selectedColumnIds = map(pdfHeaders, ({ path }) => path);

    /**
     * ItemName width = 100% - sum(all the other columns)
     */
    const sumWidths = sumBy(
      filter(
        entryFormHeaders,
        (header) =>
          includes(selectedColumnIds, header.dataKey) &&
          !isEqual(header.dataKey, STAND_WORKSHEET_PDF_HEADER_VALUES.ItemName),
      ),
      (entryFormHeader) => parseFloat(entryFormHeader.header.width),
    );
    const itemNameColumnWidth = 100 - sumWidths;
    const item = find(entryFormHeaders, [
      'dataKey',
      STAND_WORKSHEET_PDF_HEADER_VALUES.ItemName,
    ]);
    item.header.width = `${itemNameColumnWidth}%`;
  };

  const getCacheSelectItems = (): PdfSelectColumn[] => {
    return pdfSelectColumn;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const resetCallback = (pdfHeaders: PdfSelectColumn[]): void => {
    // here can do some special work reset button clicked
  };

  return {
    bulkOptions,
    data,
    generateFileName: (pdfData): string => {
      const pdfEntityString = 'Stand Worksheet';
      const siteName = get(pdfData, 'siteName');

      return getSingleExportFileName(pdfEntityString, siteName);
    },
    generateHeader: (pdfData: IPdfData, tools): Content[] =>
      compact<Content>([
        {
          text: tools.services.i18nService.instant(
            I18n.EntityNames.StandWorksheet,
          ),
          fontSize: 14,
          bold: true,
          margin: [20, 20, 20, 0],
        },
        {
          fontSize: 10,
          margin: [20, 5, 20, 0],
          columns: [
            {
              text: [
                {
                  text: tools.services.i18nService.instant(
                    I18n.Forms.GridForm.EventWithColon,
                  ),
                },
                {
                  text: pdfData.eventName,
                  bold: true,
                },
              ],
            },
            {
              text: [
                {
                  text: tools.services.i18nService.instant(
                    I18n.Forms.GridForm.StatusWithColon,
                  ),
                },
                {
                  text: tools.services.i18nService.instant(pdfData?.status),
                  bold: true,
                },
              ],
            },
            {
              text: [
                {
                  text: tools.services.i18nService.instant(
                    I18n.Forms.GridForm.EventStartWithColon,
                  ),
                },
                { text: pdfData.eventStartDate, bold: true },
              ],
            },
          ],
        },
        {
          fontSize: 10,
          margin: [20, 5, 20, 0],
          columns: [
            {
              text: [
                {
                  text: tools.services.i18nService.instant(
                    I18n.Forms.GridForm.SiteWithColon,
                  ),
                },
                {
                  text: pdfData?.fullSiteName,
                  bold: true,
                },
              ],
            },
            {
              text: [
                {
                  text: tools.services.i18nService.instant(
                    I18n.Forms.GridForm.ChargeableWithColon,
                  ),
                },
                { text: pdfData.chargeableLabel, bold: true },
              ],
            },
            {
              text: [
                {
                  text: tools.services.i18nService.instant(
                    I18n.Forms.GridForm.EventEndWithColon,
                  ),
                },
                { text: pdfData?.eventEndDate, bold: true },
              ],
            },
          ],
        },
        getSearchHeaderDefine(searched, pdfData, tools),
      ]),
    content: {
      informationListGridPartOne: {
        type: PdfContentType.Grids,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setting: (pdfData: IPdfData): PdfContentSetting => ({
          informationList: {
            title: I18n.Forms.StandWorksheetForm.InformationData,
            columnDefaultStyle: (
              col,
              gridData: IInformationData[],
            ): ColumnDefaultStyle => {
              const informationData = head(gridData);

              const formatterOriginFieldMapping = {
                [STAND_WORKSHEET_PDF_HEADER_VALUES.PhysTerminalDiff]:
                  STAND_WORKSHEET_PDF_HEADER_VALUES.PhysicalSales,
                [STAND_WORKSHEET_PDF_HEADER_VALUES.ReceiptPhysicalDiff]:
                  STAND_WORKSHEET_PDF_HEADER_VALUES.ReceiptTotal,
                [STAND_WORKSHEET_PDF_HEADER_VALUES.CashTerminalDiff]:
                  STAND_WORKSHEET_PDF_HEADER_VALUES.CashCount,
              };

              return {
                width: getInformationFormHeaderWidth(isOffPostClosureSetting),
                alignment: 'right',
                valueFormatter: (params): string =>
                  includes(
                    [
                      STAND_WORKSHEET_PDF_HEADER_VALUES.PhysTerminalDiff,
                      STAND_WORKSHEET_PDF_HEADER_VALUES.ReceiptPhysicalDiff,
                      STAND_WORKSHEET_PDF_HEADER_VALUES.CashTerminalDiff,
                    ],
                    col.path,
                  )
                    ? formatDiffNumberAndPercentage(
                        params.rowData,
                        col.path,
                        formatterOriginFieldMapping[col.path],
                        params.tools?.services?.numberFormatService,
                      )
                    : params.tools?.services?.numberFormatService?.getNumberFormatter(
                        true,
                      )(params),
                style: {
                  color: getPdfTextColor(informationData, col.path),
                },
              };
            },
            columns: getInformationPdfColumns(isOffPostClosureSetting),
          },
        }),
      },
      informationListGridPartTwo: {
        type: PdfContentType.Grids,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setting: (pdfData: IPdfData): PdfContentSetting => ({
          informationList: {
            title: {
              text: '',
              style: { margin: [0, 0, 0, -8] },
            },
            columnDefaultStyle: (
              col,
              gridData: IInformationData[],
            ): ColumnDefaultStyle => {
              const informationData = head(gridData);

              return {
                width: '11.111%',
                alignment: 'right',
                valueFormatter: (params): string =>
                  params.tools?.services?.numberFormatService?.getNumberFormatter(
                    true,
                  )(params),
                style: {
                  color: getPdfTextColor(informationData, col.path),
                },
              };
            },
            columns: getEntryFormPdfColumns(isOffPostClosureSetting),
          },
        }),
      },
      mainGrids: {
        type: PdfContentType.Grids,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setting: (pdfData: IPdfData): PdfContentSetting => ({
          entryForm: {
            dontBreakRows: false,
            title: I18n.Forms.StandWorksheetForm.EntryForm,
            subTitle: (): string[] => [
              STAND_WORKSHEET_HEADER_INFO_MESSAGE.chargeable,
              STAND_WORKSHEET_HEADER_INFO_MESSAGE.parent_product_price,
            ],
            columnDefaultStyle: (col): ColumnDefaultStyle => {
              const currencyFields = [
                STAND_WORKSHEET_PDF_HEADER_VALUES.UnitPrice,
                STAND_WORKSHEET_PDF_HEADER_VALUES.ProductPrice,
                STAND_WORKSHEET_PDF_HEADER_VALUES.TerminalDollars,
                STAND_WORKSHEET_PDF_HEADER_VALUES.PhysicalDollars,
                STAND_WORKSHEET_PDF_HEADER_VALUES.PriceAdjustment,
                STAND_WORKSHEET_PDF_HEADER_VALUES.VarianceDollars,
                STAND_WORKSHEET_PDF_HEADER_VALUES.TerminalDollars,
                STAND_WORKSHEET_PDF_HEADER_VALUES.Discounts,
                STAND_WORKSHEET_PDF_HEADER_VALUES.Taxes,
                STAND_WORKSHEET_PDF_HEADER_VALUES.PostClosureTaxes,
              ];

              const numberFields = [
                STAND_WORKSHEET_PDF_HEADER_VALUES.BeginningCount,
                STAND_WORKSHEET_PDF_HEADER_VALUES.MGRStartingOverride,
                STAND_WORKSHEET_PDF_HEADER_VALUES.DuringEventIn,
                STAND_WORKSHEET_PDF_HEADER_VALUES.DuringEventOut,
                STAND_WORKSHEET_PDF_HEADER_VALUES.Gratis,
                STAND_WORKSHEET_PDF_HEADER_VALUES.IngredientSpoil,
                STAND_WORKSHEET_PDF_HEADER_VALUES.Adjustment,
                STAND_WORKSHEET_PDF_HEADER_VALUES.ExpectedQty,
                STAND_WORKSHEET_PDF_HEADER_VALUES.EndingCount,
                STAND_WORKSHEET_PDF_HEADER_VALUES.TerminalQty,
                STAND_WORKSHEET_PDF_HEADER_VALUES.PhysicalQty,
                STAND_WORKSHEET_PDF_HEADER_VALUES.VarianceQty,
              ];

              const isCurrencyFields = includes(currencyFields, col.path);
              const isNumberFields = includes(numberFields, col.path);

              return {
                valueFormatter: (params): string =>
                  isCurrencyFields || isNumberFields
                    ? params.tools?.services?.numberFormatService.getNumberFormatter(
                        isCurrencyFields,
                      )(params)
                    : params.value,
              };
            },
            columns: map(
              entryFormHeaders,
              ({ header, dataKey, required }) =>
                <IPdfColumn>{
                  required,
                  path: dataKey,
                  alignment: header.textAlign,
                  width: header.width,
                  title: header.text,
                },
            ),
          },
        }),
      },
    },
    options: {
      useSmallerFontInTable: false,
      pageMargins: [20, searched ? 90 : 75, 20, 60],
      columnSelector: {
        onClose,
        onCacheSelectItems,
        getCacheSelectItems,
        resetCallback,
        showSelectAll: false,
        showSelectedCount: true,
        showResetButton: true,
        cacheKey: STAND_WORKSHEET_PDF_SELECTED_COLUMNS,
        twoColumnLayout: true,
        supportSelectItemsCache: true,
        tips: i18nService.instant(
          I18n.InfoMessages.YouCanSelectUpToPrintAndExport,
          {
            // eslint-disable-next-line id-blacklist
            number: STAND_WORKSHEET_PDF_SELECTED_UP_COLUMNS,
          },
        ),
        requiredNotDisabled: true,
        maxSelectCount: STAND_WORKSHEET_PDF_SELECTED_UP_COLUMNS,
        effectCondition: {
          type: 'excludes',
          paths: ['informationListGridPartOne', 'informationListGridPartTwo'],
        },
        columns: map(
          entryFormHeaders,
          (
            {
              header,
              dataKey,
              fullName,
              required,
              disabled,
              defaultDisabled = false,
            },
            index,
          ) =>
            <IPdfTableHeader>{
              required,
              disabled,
              defaultDisabled,
              title: isEqual(index, 0)
                ? GLOBAL_CHARGEABLE_TEXT
                : fullName || header.text,
              path: dataKey,
            },
        ),
      },
    },
  };
};

export { buildSinglePdfFileName, buildSWSPdfConfig };
